/**
 * Deals Metrics and Calculation Logic
 *
 * Defines completion calculation, status metrics, category determination,
 * and data availability checks for Deals.
 */

// Get deal completion metrics
export const getDealMetrics = (deal) => {
  if (!deal) return { completion: 0, completedFields: 0, totalFields: 0 };

  const requiredFields = [
    "name",
    "type",
    "status",
    "dealStructure",
    "financial.totalDealValue",
    "terms.agreementDate",
    "terms.completionDate",
  ];

  const optionalFields = [
    "description",
    "relatedProject",
    "parties.lead.name",
    "parties.partner.name",
    "financial.expectedReturn",
    "financial.irr",
    "financial.paybackPeriod",
    "terms.profitSplit",
    "terms.managementFee",
  ];

  const allFields = [...requiredFields, ...optionalFields];

  const completedFields = allFields.filter((field) => {
    const value = getNestedProperty(deal, field);
    return value !== null && value !== undefined && value !== "";
  }).length;

  const completion = Math.round((completedFields / allFields.length) * 100);

  const completedRequiredFields = requiredFields.filter((field) => {
    const value = getNestedProperty(deal, field);
    return value !== null && value !== undefined && value !== "";
  }).length;

  return {
    completion,
    completedFields,
    totalFields: allFields.length,
    requiredFields: requiredFields.length,
    completedRequiredFields,
  };
};

// Helper function to get nested property values
const getNestedProperty = (obj, path) => {
  return path.split(".").reduce((current, prop) => current?.[prop], obj);
};

// Calculate overall metrics for a list of deals
export const calculateDealsMetrics = (deals = []) => {
  const total = deals.length;

  const statusCounts = deals.reduce((acc, deal) => {
    const status = deal.status || "draft";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const typeCounts = deals.reduce((acc, deal) => {
    const type = deal.type || "acquisition";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  // Calculate average completion
  const totalCompletion = deals.reduce((sum, deal) => {
    return sum + getDealMetrics(deal).completion;
  }, 0);

  const averageCompletion = total > 0 ? Math.round(totalCompletion / total) : 0;

  // Calculate total deal value
  const totalDealValue = deals.reduce((sum, deal) => {
    return sum + (deal.financial?.totalDealValue || 0);
  }, 0);

  // Calculate average IRR
  const dealsWithIRR = deals.filter((deal) => deal.financial?.irr);
  const averageIRR =
    dealsWithIRR.length > 0
      ? Math.round(
          (dealsWithIRR.reduce((sum, deal) => sum + deal.financial.irr, 0) /
            dealsWithIRR.length) *
            10
        ) / 10
      : 0;

  return {
    total,
    draft: statusCounts.draft || 0,
    active: statusCounts.active || 0,
    "due-diligence": statusCounts["due-diligence"] || 0,
    completed: statusCounts.completed || 0,
    cancelled: statusCounts.cancelled || 0,

    // Type breakdown
    acquisition: typeCounts.acquisition || 0,
    disposal: typeCounts.disposal || 0,
    "joint-venture": typeCounts["joint-venture"] || 0,
    development: typeCounts.development || 0,
    refinancing: typeCounts.refinancing || 0,

    // Financial metrics
    totalDealValue,
    averageCompletion,
    averageIRR,

    // Status percentages
    activePercentage:
      total > 0 ? Math.round(((statusCounts.active || 0) / total) * 100) : 0,
    completedPercentage:
      total > 0 ? Math.round(((statusCounts.completed || 0) / total) * 100) : 0,
  };
};

// Determine deal category for icons
export const getDealCategory = (deal) => {
  if (!deal.type) return "acquisition";

  const type = deal.type.toLowerCase();

  // Map deal types to categories for icons
  const categoryMap = {
    acquisition: "acquisition",
    disposal: "disposal",
    "joint-venture": "joint-venture",
    development: "development",
    refinancing: "refinancing",
    purchase: "acquisition",
    sale: "disposal",
    partnership: "joint-venture",
    finance: "refinancing",
  };

  return categoryMap[type] || "acquisition";
};

// Check data availability for a deal
export const checkDealDataAvailability = (deal) => {
  const metrics = getDealMetrics(deal);

  return {
    hasBasicInfo: !!(deal.name && deal.type && deal.status),
    hasFinancials: !!deal.financial?.totalDealValue,
    hasParties: !!(deal.parties?.lead?.name || deal.parties?.buyer?.name),
    hasTerms: !!(deal.terms?.agreementDate && deal.terms?.completionDate),
    hasProject: !!deal.relatedProject,
    completionLevel: metrics.completion,

    // Quality indicators
    isComplete: metrics.completion === 100,
    isWellFormed: metrics.completion >= 80,
    needsAttention: metrics.completion < 50,

    // Missing critical data
    missingCritical: metrics.completedRequiredFields < metrics.requiredFields,
  };
};

// Generate status summary for deals
export const getDealsStatusSummary = (deals = []) => {
  const metrics = calculateDealsMetrics(deals);

  return [
    {
      label: "Total Deals",
      value: metrics.total,
      type: "count",
      color: "blue",
    },
    {
      label: "Active",
      value: metrics.active,
      percentage: metrics.activePercentage,
      type: "status",
      color: "green",
    },
    {
      label: "Completed",
      value: metrics.completed,
      percentage: metrics.completedPercentage,
      type: "status",
      color: "gray",
    },
    {
      label: "Total Value",
      value: new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(metrics.totalDealValue),
      type: "currency",
      color: "indigo",
    },
  ];
};
